import React, { createContext, useContext, useState, useEffect } from 'react';
import type { Canton, CantonDefault, InsuranceWithRate } from '@/types/insurance';
import { useCantonDefaults } from '@/hooks/useCantonDefaults';
import { useInsuranceTypes } from '@/hooks/useInsuranceTypes';

interface CantonContextType {
  selectedCanton: Canton | null;
  setSelectedCanton: (canton: Canton | null) => void;
  getDefaultRate: (insuranceTypeId: string, employeeAge?: number, salary?: number) => number;
  getInsurancesWithDefaults: (employeeAge?: number, salary?: number) => InsuranceWithRate[];
  isLoading: boolean;
}

const CantonContext = createContext<CantonContextType | undefined>(undefined);

export const useCantonContext = () => {
  const context = useContext(CantonContext);
  if (!context) {
    throw new Error('useCantonContext must be used within a CantonProvider');
  }
  return context;
};

interface CantonProviderProps {
  children: React.ReactNode;
  defaultCanton?: Canton;
}

export const CantonProvider: React.FC<CantonProviderProps> = ({ 
  children, 
  defaultCanton = 'ZH' 
}) => {
  const [selectedCanton, setSelectedCanton] = useState<Canton | null>(defaultCanton);
  
  const { data: cantonDefaults, isLoading: defaultsLoading } = useCantonDefaults(selectedCanton || undefined);
  const { data: insuranceTypes, isLoading: typesLoading } = useInsuranceTypes();

  const isLoading = defaultsLoading || typesLoading;

  const getDefaultRate = (
    insuranceTypeId: string, 
    employeeAge?: number, 
    salary?: number
  ): number => {
    if (!cantonDefaults) return 0;

    const defaultConfig = cantonDefaults.find(
      (def) => def.insurance_type_id === insuranceTypeId
    );

    if (!defaultConfig) return 0;

    // Check age constraints
    if (employeeAge) {
      if (defaultConfig.min_age && employeeAge < defaultConfig.min_age) return 0;
      if (defaultConfig.max_age && employeeAge > defaultConfig.max_age) return 0;
    }

    // Check salary threshold constraints (e.g., ALV/ALVZ have salary caps)
    if (salary && defaultConfig.salary_threshold && salary > defaultConfig.salary_threshold) {
      return 0;
    }

    // Special logic for BVG based on age
    if (defaultConfig.insurance_type_id && employeeAge) {
      const insuranceType = insuranceTypes?.find(t => t.id === defaultConfig.insurance_type_id);
      if (insuranceType?.code === 'BVG_PK') {
        // BVG rates by age: 25-34: 7%, 35-44: 10%, 45-54: 15%, 55-65: 18%
        if (employeeAge >= 25 && employeeAge <= 34) return 7.0;
        if (employeeAge >= 35 && employeeAge <= 44) return 10.0;
        if (employeeAge >= 45 && employeeAge <= 54) return 15.0;
        if (employeeAge >= 55 && employeeAge <= 65) return 18.0;
        return 0; // Outside BVG age range
      }
    }

    return defaultConfig.default_rate_percent;
  };

  const getInsurancesWithDefaults = (
    employeeAge?: number, 
    salary?: number
  ): InsuranceWithRate[] => {
    if (!insuranceTypes || !cantonDefaults) return [];

    return insuranceTypes.map((insurance) => {
      const defaultRate = getDefaultRate(insurance.id, employeeAge, salary);
      
      return {
        ...insurance,
        rate_percent: defaultRate,
        is_enabled: insurance.is_mandatory || defaultRate > 0,
        is_custom_rate: false
      };
    });
  };

  const value: CantonContextType = {
    selectedCanton,
    setSelectedCanton,
    getDefaultRate,
    getInsurancesWithDefaults,
    isLoading
  };

  return (
    <CantonContext.Provider value={value}>
      {children}
    </CantonContext.Provider>
  );
};